// Авторизация по токенам Laravel Sanctum.
// Вход: POST /auth/login -> { token, expires_at, user }. Токен хранится в localStorage
// и подставляется во все запросы заголовком Authorization (см. http.js).
import Vue from 'vue'
import axios from 'axios'

const TOKEN_KEY = 'api_token'
const USER_KEY = 'api_user'
const EXPIRES_KEY = 'api_token_expires_at'
const DEVICE_KEY = 'device_id'

// Ключ старой временной авторизации (логин/пароль были зашиты в код) — убираем.
const LEGACY_KEY = 'auth_token'

function read(key) {
  try {
    return localStorage.getItem(key)
  } catch (e) {
    return null
  }
}

function write(key, value) {
  try {
    localStorage.setItem(key, value)
  } catch (e) {
    // localStorage недоступен (приватный режим и т.п.) — вход не сохранится
  }
}

function remove(key) {
  try {
    localStorage.removeItem(key)
  } catch (e) {
    // ничего
  }
}

export function getToken() {
  return read(TOKEN_KEY)
}

function readUser() {
  try {
    return JSON.parse(read(USER_KEY))
  } catch (e) {
    return null
  }
}

// Текущий пользователь хранится реактивно: когда права обновляются (см. refreshUser),
// меню и кнопки в шаблонах перерисовываются сами.
const state = Vue.observable({ user: readUser() })

export function getUser() {
  return state.user
}

function setUser(user) {
  state.user = user || null
  if (user) write(USER_KEY, JSON.stringify(user))
  else remove(USER_KEY)
}

// Есть ли у пользователя разрешение: страница (page.orders, ...) или функция (orders.assign, export.excel, ...).
export function can(permission) {
  const user = state.user
  return !!(user && Array.isArray(user.permissions) && user.permissions.indexOf(permission) !== -1)
}

// Подтягивает актуальные права с сервера: администратор мог изменить их после входа.
// Ошибки сети не мешают работе — остаются прежние права (сервер всё равно проверяет каждый запрос).
export function refreshUser() {
  if (!getToken()) return Promise.resolve(null)
  return axios
    .get('/auth/me')
    .then((res) => {
      if (res.data && res.data.user) setUser(res.data.user)
      // сервер продлевает сессию при каждом открытии приложения: запоминаем новый срок, иначе устройство «забудет» вход раньше времени
      if (res.data && res.data.expires_at) write(EXPIRES_KEY, res.data.expires_at)
      return state.user
    })
    .catch(() => state.user)
}

export function isAuthenticated() {
  if (!getToken()) return false
  const expiresAt = read(EXPIRES_KEY)
  if (expiresAt && new Date(expiresAt).getTime() <= Date.now()) {
    clearSession()
    return false
  }
  return true
}

export function clearSession() {
  // выход или протухшая сессия: блокировка относится к прежнему пользователю и со входом не переносится
  remove('app_locked')
  remove(TOKEN_KEY)
  setUser(null)
  remove(EXPIRES_KEY)
  remove(LEGACY_KEY)
}

// Запоминает сессию из ответа сервера (вход по паролю или по ПИН-коду).
function startSession(data) {
  write(TOKEN_KEY, data.token)
  setUser(data.user)
  if (data.expires_at) write(EXPIRES_KEY, data.expires_at)
  else remove(EXPIRES_KEY)
  remove(LEGACY_KEY)
  return data.user
}

// Возвращает Promise<user>; при ошибке — Error с понятным сообщением.
export function login(username, password) {
  return axios
    .post('/auth/login', { username, password })
    .then((res) => startSession(res.data))
    .catch((err) => {
      const data = err.response && err.response.data
      throw new Error((data && data.error) || 'Не удалось выполнить вход. Проверьте соединение.')
    })
}

// Выход: токен отзывается на сервере (не дожидаясь ответа), локальная сессия очищается сразу.
export function logout() {
  const token = getToken()
  clearSession()
  if (token) {
    axios.post('/auth/logout', null, { headers: { Authorization: 'Bearer ' + token } }).catch(() => {})
  }
}

// Идентификатор этого браузера для экрана блокировки: по нему сервер блокирует устройство после неверных ПИН-кодов.
export function deviceId() {
  let id = read(DEVICE_KEY)
  if (!id || !/^[A-Za-z0-9_-]{8,64}$/.test(id)) {
    const bytes = new Uint8Array(16)
    if (window.crypto && window.crypto.getRandomValues) window.crypto.getRandomValues(bytes)
    else for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256)
    id = Array.prototype.map.call(bytes, (b) => ('0' + b.toString(16)).slice(-2)).join('')
    write(DEVICE_KEY, id)
  }
  return id
}

function deviceHeaders() {
  return { headers: { 'X-Device-Id': deviceId() } }
}

// Экран блокировки: сколько секунд устройство ещё заблокировано; со списком быстрого выбора (withUsers) — для смены пользователя.
export function pinStatus(withUsers) {
  return axios.get('/auth/pin', Object.assign({ params: withUsers ? { users: 1 } : {} }, deviceHeaders())).then((res) => res.data)
}

// Вход по ПИН-коду. Свой код — вернётся { switched: false }, чужой — сессия меняется на этого пользователя ({ switched: true }).
// При ошибке — Error с текстом сервера, а также bannedFor (секунды блокировки) и attemptsLeft.
export function pinLogin(pin, userId) {
  return axios
    .post('/auth/pin', { pin, user_id: userId || null }, deviceHeaders())
    .then((res) => {
      if (res.data.switched) startSession(res.data)
      else if (res.data.user) setUser(res.data.user)
      return res.data
    })
    .catch((err) => {
      const data = err.response && err.response.data
      const error = new Error((data && (data.error || data.message)) || 'Не удалось проверить ПИН-код. Проверьте соединение.')
      error.bannedFor = (data && data.banned_for) || 0
      error.attemptsLeft = data && data.attempts_left
      error.fatal = !!err.response && err.response.status === 401
      throw error
    })
}

// Другая вкладка сменила пользователя или вышла: эта вкладка перезагружается и показывает актуального пользователя.
window.addEventListener('storage', (e) => {
  if (e.key === TOKEN_KEY && e.newValue !== e.oldValue) window.location.reload()
})
